import React, { useEffect, useRef, useState } from "react";
import useFetch from "use-http";
import { useCookies } from "react-cookie";

import {
	ACCESS_TOKEN_COOKIE,
	REFRESH_TOKEN_COOKIE,
} from "common/constants/cookies";
import useFetchContext from "contexts/FetchProvider/useFetchContext";
import { getCookieDomain } from "utils/getDomain";
import BootGate from "contexts/BootGate";
import { setStaffFlag } from "components/AppError";
import isHegiasStaffEmail from "utils/isHegiasStaffEmail";
import Context from "./context";
import { AuthState, LoginResponseDTO, User } from "./types";

declare global {
	interface Window {
		$crisp: any;
		CRISP_WEBSITE_ID: any;
	}
}

const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [cookies, setCookie, removeCookie] = useCookies([
		ACCESS_TOKEN_COOKIE,
		REFRESH_TOKEN_COOKIE,
	]);
	const [auth, setAuth] = useState<AuthState | null>({
		token: cookies[ACCESS_TOKEN_COOKIE],
		refreshToken: cookies[REFRESH_TOKEN_COOKIE],
	});
	const [me, setMe] = useState<User | null>(null);

	const { setHeader } = useFetchContext();

	const { request, loading, response } = useFetch(
		"/auth/login",
		(globalOptions: any) => {
			const options = { ...globalOptions };
			delete options.headers?.Authorization;
			return options;
		},
	);

	const {
		get: getUser,
		error: userError,
		response: userResponse,
	} = useFetch("/users/me");

	const setAuthData = (token: string, refreshToken: string) => {
		setHeader("Authorization", `Bearer ${token}`);

		setAuth({
			token,
			refreshToken,
		});

		setCookie(ACCESS_TOKEN_COOKIE, token, {
			path: "/",
			domain: getCookieDomain(),
		});

		setCookie(REFRESH_TOKEN_COOKIE, refreshToken, {
			path: "/",
			domain: getCookieDomain(),
		});
	};

	const login = async (email: string, password: string) => {
		await request.post("", { email, password });

		if (!response?.ok) {
			throw new Error(
				response.data?.message || "Error. Please try again.",
			);
		}
		const result = (await response.json()) as LoginResponseDTO;

		const {
			message: { token, refreshToken },
		} = result;

		setAuthData(token, refreshToken);

		window.$crisp.push(["do", "session:reset"]);

		return response.data;
	};

	const logout = async () => {
		setAuth(null);
		setMe(null);
		removeCookie(ACCESS_TOKEN_COOKIE, {
			path: "/",
			domain: getCookieDomain(),
		});

		window.$crisp.push(["do", "session:reset"]);
		setStaffFlag(false);

		return Promise.resolve();
	};

	const fetchUser = async () => {
		const user: User = await getUser();
		if (!user || !user.profile) {
			return;
		}

		window?.$crisp?.push([
			"set",
			"user:nickname",
			[`${user.profile.name} ${user.profile.lastName}`],
		]);
		window?.$crisp?.push([
			"set",
			"user:avatar",
			[
				`${process.env.REACT_APP_AWS_S3_URL}/users/${user.shortid}/${user.profile.avatar}`,
			],
		]);
		window?.$crisp?.push(["set", "user:email", [user.profile.email[0]]]);

		// Remembered so the error page can offer the Details control before the
		// app has a backend to ask. A rendering hint only - the server checks a
		// signed token claim on every /health/detailed call.
		setStaffFlag(isHegiasStaffEmail(user.profile.email));

		setMe(user);
	};

	useEffect(() => {
		if (!window.$crisp) {
			return;
		}

		window.$crisp.push([
			"on",
			"chat:opened",
			() => {
			window.$crisp.push(["do", "helpdesk:search"]);
			},
		]);
	}, []);

	useEffect(() => {
		/* window.$crisp = [{}]; */
		window.CRISP_WEBSITE_ID = process.env.REACT_APP_CRISP_KEY ?? "";

		const d = document;
		const s = d.createElement("script");
		s.src = "https://client.crisp.chat/l.js";
		s.async = true;
		d.getElementsByTagName("head")[0].appendChild(s);
		window?.$crisp?.push(["safe", true])

		if (auth?.token) {
			setHeader("Authorization", `Bearer ${auth.token}`);
			setCookie(ACCESS_TOKEN_COOKIE, auth.token, {
				path: "/",
				domain: getCookieDomain(),
			});
			setCookie(REFRESH_TOKEN_COOKIE, auth.refreshToken, {
				path: "/",
				domain: getCookieDomain(),
			});
			fetchUser();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auth]);

	// An unverified account usually gets verified somewhere else — the mail's
	// link opens a new tab, or another device — while this tab keeps the stale
	// `me` and with it the locked Navigator and the missing "add scene".
	// Re-checked whenever the tab becomes visible again, which is exactly the
	// moment the user comes back from their mailbox: no polling, and nothing
	// at all once the account is verified.
	//
	// Its own request on purpose: a failure here (a blip while switching tabs)
	// must not reach sessionRestoreFailed below and replace the app with the
	// error page. Throttled, since visibility can flip several times in a row.
	const { get: checkUser } = useFetch("/users/me");
	const lastVerificationCheck = useRef(0);
	const awaitingVerification = me?.profile?.emailVerified === false;
	useEffect(() => {
		if (!awaitingVerification) {
			return undefined;
		}
		const onVisibilityChange = async () => {
			if (document.visibilityState !== "visible") {
				return;
			}
			const now = Date.now();
			if (now - lastVerificationCheck.current < 10000) {
				return;
			}
			lastVerificationCheck.current = now;
			try {
				const user: User = await checkUser();
				if (user?.profile?.emailVerified === true) {
					setMe(user);
				}
			} catch (_) {
				// Next visit tries again.
			}
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () =>
			document.removeEventListener("visibilitychange", onVisibilityChange);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [awaitingVerification]);

	// A failed /users/me only means an outage when there was a session to
	// restore in the first place. With no token it is the ordinary anonymous
	// path, and a 401/403 means the session simply ended - both belong on the
	// login screen, not behind a full-page error.
	const sessionRestoreFailed =
		!!auth?.token &&
		(!!userError ||
			(!!userResponse?.status && userResponse.status >= 500));

	return (
		<BootGate
			label="Auth"
			error={sessionRestoreFailed ? userError : undefined}
			response={sessionRestoreFailed ? userResponse : undefined}
			onRetry={fetchUser}
		>
			<Context.Provider
				value={{
					me,
					auth,
					authenticated: !!auth?.token,
					loading,
					login,
					logout,
					setAuthData,
					fetchUser,
				}}
			>
				{children}
			</Context.Provider>
		</BootGate>
	);
};

export default AuthProvider;
